import React from 'react';
import { motion } from 'framer-motion';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import HeroSection from './components/HeroSection';
import CTASection from './components/CTASection';

const OurStoryPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Hero Section */}
      <HeroSection />
      {/* Main Content */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Introduction Section - Enhanced with Scroll Animations */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#D93B34]/10 to-[#9B8E56]/10 rounded-full px-8 py-4 mb-8">
                <Icon name="BookOpen" size={28} className="text-[#D93B34]" />
                <span className="text-xl font-semibold text-[#D93B34]">Notre Histoire Exceptionnelle</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8">
                À Propos de
                <span className="block text-transparent bg-gradient-to-r from-[#D93B34] via-[#9B8E56] to-amber-600 bg-clip-text">
                  CRF Perfection
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-[#9B8E56] font-semibold mb-10 max-w-5xl mx-auto">
                De l'Expertise Pionnière au Renouveau Stratégique par l'Intelligence Artificielle
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div 
                className="space-y-8"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <p className="text-xl text-gray-700 leading-relaxed">
                  Découvrez l'évolution extraordinaire du Cabinet CRF Perfection, une histoire captivante de croissance stratégique, d'innovation technologique et de transformation organisationnelle 
                  qui s'étend sur plus de 18 ans d'expertise reconnue dans le domaine du conseil de haut niveau 
                  et de la formation professionnelle d'excellence.
                </p>
                
                {/* Mission Card */}
                <motion.div 
                  className="bg-gradient-to-br from-blue-50 via-blue-100/50 to-indigo-50 border-l-4 border-[#D93B34] p-8 rounded-r-2xl shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="bg-[#D93B34] rounded-full p-3">
                      <Icon name="Target" size={24} className="text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3 text-2xl">Notre Mission Stratégique :</h3>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Accompagner les entreprises dans leur transformation numérique et organisationnelle 
                    à travers un conseil stratégique visionnaire, des solutions innovantes et une expertise 
                    technologique avancée, parfaitement adaptées aux défis complexes du 21ème siècle.
                  </p>
                </motion.div>
                
                {/* Vision Card */}
                <motion.div 
                  className="bg-gradient-to-br from-amber-50 via-[#9B8E56]/10 to-amber-100/50 border-l-4 border-[#9B8E56] p-8 rounded-r-2xl shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="bg-[#9B8E56] rounded-full p-3">
                      <Icon name="Eye" size={24} className="text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3 text-2xl">Notre Vision d'Avenir :</h3>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Être le partenaire de référence mondiale pour l'innovation technologique, la digitalisation avancée et l'excellence opérationnelle, en intégrant l'Intelligence Artificielle et les 
                    technologies émergentes au cœur de nos services stratégiques et de nos solutions sur mesure.
                  </p>
                </motion.div>
                
                {/* Values Card */}
                <motion.div 
                  className="bg-gradient-to-br from-yellow-50 via-amber-100/50 to-yellow-100/50 border-l-4 border-amber-500 p-8 rounded-r-2xl shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="bg-amber-500 rounded-full p-3">
                      <Icon name="Heart" size={24} className="text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3 text-2xl">Nos Valeurs Fondamentales :</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={20} className="text-amber-600" />
                      <span className="text-gray-700 font-medium">Excellence & Innovation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={20} className="text-amber-600" />
                      <span className="text-gray-700 font-medium">Intégrité & Transparence</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={20} className="text-amber-600" />
                      <span className="text-gray-700 font-medium">Collaboration & Partenariat</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={20} className="text-amber-600" />
                      <span className="text-gray-700 font-medium">Impact & Transformation</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
              
              {/* Enhanced Visual */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="bg-gradient-to-br from-white via-gray-50 to-amber-50/30 rounded-3xl p-12 shadow-2xl border-2 border-[#9B8E56]/20">
                  <div className="text-center space-y-8">
                    {/* Company Logo/Icon */}
                    <motion.div 
                      className="relative"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      viewport={{ once: true }}
                    >
                      <div className="bg-gradient-to-r from-[#D93B34] via-[#9B8E56] to-amber-500 rounded-full p-8 mx-auto w-fit shadow-2xl">
                        <Icon name="Building2" size={80} className="text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 bg-amber-400 rounded-full p-2 animate-pulse">
                        <Icon name="Sparkles" size={20} className="text-white" />
                      </div>
                    </motion.div>
                    
                    {/* Company Info */}
                    <motion.div 
                      className="space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-3xl font-bold text-gray-900">CRF PERFECTION</h2>
                      <p className="text-[#9B8E56] font-semibold text-lg">Centre de Recherche et de Formation</p>
                      <div className="w-20 h-1 bg-gradient-to-r from-[#D93B34] to-amber-500 rounded-full mx-auto"></div>
                    </motion.div>
                    
                    {/* Key Stats */}
                    <motion.div 
                      className="grid grid-cols-2 gap-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      viewport={{ once: true }}
                    >
                      <div className="bg-gradient-to-r from-red-50 to-red-100/50 rounded-xl p-6 border border-[#D93B34]/20">
                        <p className="text-3xl font-bold text-[#D93B34]">2007</p>
                        <p className="text-sm text-gray-600 font-medium">Année de Création</p>
                      </div>
                      <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl p-6 border border-[#9B8E56]/20">
                        <p className="text-3xl font-bold text-[#9B8E56]">18+</p>
                        <p className="text-sm text-gray-600 font-medium">Années d'Excellence</p>
                      </div>
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-100/50 rounded-xl p-6 col-span-2 border border-amber-500/20">
                        <p className="text-3xl font-bold text-amber-600">Excellence</p>
                        <p className="text-sm text-gray-600 font-medium">Reconnue Internationalement</p>
                      </div>
                    </motion.div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl p-4 shadow-xl"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  viewport={{ once: true }}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={24} className="text-white" />
                    <div className="text-sm font-bold text-white">
                      <p>Depuis</p>
                      <p>2007</p>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="absolute -bottom-4 -left-4 bg-gradient-to-r from-white to-gray-100 rounded-2xl p-4 shadow-xl border-2 border-[#D93B34]/20"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  viewport={{ once: true }}
                >
                  <Image
                    src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                    alt="CRF PERFECTION logo featuring interlocking heart-shaped design representing Centre de Recherche et de Formation excellence"
                    className="w-12 h-12 object-contain"
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      {/* CTA Section */}
      <CTASection />
      {/* Enhanced Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <Image
                  src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                  alt="CRF PERFECTION logo featuring interlocking heart-shaped design representing Centre de Recherche et de Formation excellence"
                  className="w-16 h-16 object-contain"
                />
                <div>
                  <h3 className="text-2xl font-bold text-white">CRF PERFECTION</h3>
                  <p className="text-amber-400 font-medium">Centre de Recherche et de Formation</p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Plus de 18 ans d'expertise exceptionnelle au service de votre transformation 
                stratégique et de votre croissance durable. L'excellence reconnue mondialement.
              </p>
              <div className="flex space-x-4">
                <div className="bg-[#D93B34] rounded-full p-2">
                  <Icon name="Award" size={20} className="text-white" />
                </div>
                <div className="bg-[#9B8E56] rounded-full p-2">
                  <Icon name="Globe" size={20} className="text-white" />
                </div>
                <div className="bg-amber-500 rounded-full p-2">
                  <Icon name="Zap" size={20} className="text-white" />
                </div>
              </div>
            </div>

            {/* Our Journey */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-b-2 border-[#D93B34] pb-2">Notre Parcours Historique</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#fondations" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="ArrowRight" size={16} />
                    <span>Les Fondations (2007-2014)</span>
                  </a>
                </li>
                <li>
                  <a href="#naissance" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="ArrowRight" size={16} />
                    <span>Naissance CRF Perfection</span>
                  </a>
                </li>
                <li>
                  <a href="#expansion" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="ArrowRight" size={16} />
                    <span>Expansion Internationale</span>
                  </a>
                </li>
                <li>
                  <a href="#innovation" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="ArrowRight" size={16} />
                    <span>Renaissance & Innovation IA</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Milestones */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-b-2 border-[#9B8E56] pb-2">Jalons Stratégiques</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#sinus" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="Star" size={16} />
                    <span>Création Sinus International</span>
                  </a>
                </li>
                <li>
                  <a href="#washington" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="Globe" size={16} />
                    <span>Agence Washington D.C.</span>
                  </a>
                </li>
                <li>
                  <a href="#digitalisation" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="Zap" size={16} />
                    <span>Transformation Digitale RH</span>
                  </a>
                </li>
                <li>
                  <a href="#conferences" className="text-gray-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="Users" size={16} />
                    <span>Conférences & Formations</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact & Legacy */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-b-2 border-amber-500 pb-2">Héritage & Contact</h4>
              <div className="space-y-4">
                <a href="/timeline" className="flex items-center space-x-3 text-gray-300 hover:text-amber-400 transition-colors duration-200">
                  <Icon name="Clock" size={20} />
                  <span>Chronologie Complète</span>
                </a>
                <a href="/achievements" className="flex items-center space-x-3 text-gray-300 hover:text-amber-400 transition-colors duration-200">
                  <Icon name="Trophy" size={20} />
                  <span>Nos Réalisations</span>
                </a>
                <a href="/contact" className="flex items-center space-x-3 text-gray-300 hover:text-amber-400 transition-colors duration-200">
                  <Icon name="MessageSquare" size={20} />
                  <span>Rejoignez Notre Histoire</span>
                </a>
                <a href="/innovation" className="flex items-center space-x-3 text-gray-300 hover:text-amber-400 transition-colors duration-200">
                  <Icon name="Lightbulb" size={20} />
                  <span>Innovation & IA</span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-700 mt-16 pt-10">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-gray-400 mb-6 md:mb-0 text-center md:text-left">
                © 2024 CRF Perfection - Notre Histoire d'Excellence. Tous droits réservés. 
                <span className="block md:inline md:ml-2 text-amber-400">
                  18 ans d'expertise, une vision d'avenir.
                </span>
              </p>
              <div className="flex flex-wrap gap-6 justify-center">
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  Politique de Confidentialité
                </a>
                <a href="/legacy" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  Notre Héritage
                </a>
                <a href="/careers" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                  Carrières
                </a>
              </div>
            </div>
            
            {/* Bottom decoration */}
            <div className="mt-8 text-center">
              <div className="w-32 h-1 bg-gradient-to-r from-[#D93B34] via-[#9B8E56] to-amber-500 rounded-full mx-auto"></div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OurStoryPage;